import { useEffect, useMemo, useState } from "react";
import { Mail, Phone, Trash2, Check, Download, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { key: "all", label: "All" },
  { key: "lead", label: "Leads" },
  { key: "general", label: "Inquiries" },
];

export default function AdminInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/inquiries");
      setItems(data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (tab === "all") return items;
    if (tab === "lead") return items.filter((i) => i.type === "lead");
    return items.filter((i) => i.type !== "lead");
  }, [items, tab]);

  const counts = useMemo(() => ({
    all: items.length,
    lead: items.filter((i) => i.type === "lead").length,
    general: items.filter((i) => i.type !== "lead").length,
  }), [items]);

  const markHandled = async (id) => {
    await api.patch(`/admin/inquiries/${id}`, null, { params: { status: "handled" } });
    toast.success("Marked as handled");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/admin/inquiries/${id}`);
    toast.success("Deleted");
    load();
  };

  const exportCsv = async () => {
    const url = `/admin/inquiries/export.csv${tab !== "all" ? `?type=${tab}` : ""}`;
    try {
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `builddirect-${tab}-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div data-testid="admin-inquiries-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5 sm:mb-6">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-slate-950 mb-1">Inquiries & Leads</h1>
          <p className="text-slate-500 text-sm">Project inquiries plus lead-capture popup submissions.</p>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          data-testid="export-csv-btn"
          className="inline-flex items-center justify-center gap-2 bg-slate-950 hover:bg-slate-800 text-white px-4 py-2 rounded-md text-sm font-medium w-full sm:w-auto"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Tabs (scrollable on mobile if needed) */}
      <div className="flex items-center gap-1 mb-5 border-b border-slate-200 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`inquiry-tab-${t.key}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex-shrink-0 ${
              tab === t.key
                ? "border-slate-950 text-slate-950"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {t.label} <span className="ml-1 text-xs text-slate-400">{counts[t.key]}</span>
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-slate-400 text-sm">No entries.</div>
        ) : filtered.map((i) => (
          <div key={i.id} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`inquiry-row-${i.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <span className="font-medium text-slate-950">{i.name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider ${
                    i.type === "lead" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
                  }`}>{i.type}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider ${
                    i.status === "handled" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                  }`}>{i.status}</span>
                </div>
                <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500 flex-wrap">
                  {i.email && <a href={`mailto:${i.email}`} className="inline-flex items-center gap-1 hover:text-slate-900"><Mail className="w-3 h-3" />{i.email}</a>}
                  {i.phone && <a href={`https://wa.me/${i.phone.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-slate-900"><Phone className="w-3 h-3" />{i.phone}</a>}
                  {i.page_url && <span className="inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /><span className="font-mono">{i.page_url}</span></span>}
                </div>
                {i.project_title && <div className="text-xs text-slate-500 mt-1">Re: <span className="text-slate-700">{i.project_title}</span></div>}
                {i.message && <p className="text-slate-700 mt-3 text-sm leading-relaxed whitespace-pre-line">{i.message}</p>}
                <div className="text-xs text-slate-400 mt-2">{new Date(i.created_at).toLocaleString()}</div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {i.status !== "handled" && (
                  <button onClick={() => markHandled(i.id)} data-testid={`handle-${i.id}`} title="Mark handled" className="p-2 hover:bg-green-50 rounded text-green-600">
                    <Check className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => remove(i.id)} data-testid={`delete-inq-${i.id}`} title="Delete" className="p-2 hover:bg-rose-50 rounded text-rose-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
